import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useQuizStore from '../stores/useQuizStore';
import { useToast } from '../components/ToastContainer';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';

const InstructorQuizCreatePage = () => {
  const toast = useToast();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { lessonId, quizId } = useParams();
  const {
    fetchQuiz,
    createQuiz,
    updateQuiz,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    isLoading,
    error,
  } = useQuizStore();

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQuizCreated, setIsQuizCreated] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    timeLimit: 30,
    attemptsAllowed: 1,
    passingScore: 70
  });

  // const [quizId, setQuizId] = useState(null);
  const [isTempLesson, setIsTempLesson] = useState(false);

  // Question management state
  const [questions, setQuestions] = useState([]);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState({
    type: 'multiple-choice',
    question: '',
    options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }],
    correctAnswer: '',
    points: 1,
    explanation: ''
  });

  // Load existing quiz if editing
  const loadQuizData = useCallback(async (id) => {
    try {
      const quizData = await fetchQuiz(id);
      if (quizData) {
        setFormData({
          title: quizData.title || "",
          description: quizData.description || "",
          timeLimit: quizData.timeLimit || 30,
          attemptsAllowed: quizData.attemptsAllowed || 1,
          passingScore: quizData.passingScore || 70,
        });
        setQuestions(quizData.questions || []);
      }
    } catch (error) {
      console.error("Error loading quiz data:", error);
      toast.error("Failed to load quiz data");
    }
  },
    [fetchQuiz, toast]
  );

  useEffect(() => {
    if (quizId) {
      setIsEditing(true);
      setIsQuizCreated(true);
      loadQuizData(quizId);
    }
  }, [quizId, loadQuizData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'timeLimit' || name === 'attemptsAllowed' || name === 'passingScore'
        ? Number(value)
        : value
    }));
  };

  const handleQuestionChange = (field, value) => {
    setCurrentQuestion(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index].text = value;
    setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
  };

  const toggleCorrectOption = (index) => {
    const newOptions = currentQuestion.options.map((opt, i) => ({
      ...opt,
      isCorrect: i === index
    }));
    setCurrentQuestion(prev => ({
      ...prev,
      options: newOptions,
      correctAnswer: newOptions[index].text
    }));
  };

  const addOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false }]
    }));
  };

  const removeOption = (index) => {
    if (currentQuestion.options.length <= 2) return;
    const newOptions = currentQuestion.options.filter((_, i) => i !== index);
    setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
  };

  const resetQuestionForm = () => {
    setCurrentQuestion({
      type: 'multiple-choice',
      question: '',
      options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }],
      correctAnswer: '',
      points: 1,
      explanation: ''
    });
    setEditingQuestionId(null);
  };

  const validateQuestion = (question) => {
    if (!question.question.trim()) {
      toast.error('Please enter a question');
      return false;
    }

    if (question.type === 'multiple-choice') {
      if (question.options.some(opt => !opt.text.trim())) {
        toast.error('Please fill in all options');
        return false;
      }
      if (!question.options.some(opt => opt.isCorrect)) {
        toast.error('Please select a correct answer');
        return false;
      }
    }

    if (question.type === 'true-false' && !['True', 'False'].includes(question.correctAnswer)) {
      toast.error('Please select a correct answer');
      return false;
    }

    return true;
  };

  const saveQuestion = async () => {
    if (!validateQuestion(currentQuestion)) return;

    try {
      if (editingQuestionId) {
        // Update existing question
        await updateQuestion(quizId, editingQuestionId, currentQuestion);
        setQuestions(prev =>
          prev.map(q => q?._id === editingQuestionId ? { ...currentQuestion, _id: editingQuestionId } : q)
        );
        toast.success('Question updated successfully');
      } else if (isEditing && quizId) {
        // Add new question to existing quiz
        const result = await addQuestion(quizId, currentQuestion);
        if (result?.success) {
          setQuestions(prev => [...prev, { ...currentQuestion, _id: result.question?._id }]);
          toast.success('Question added successfully');
        }
      }
      resetQuestionForm();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Failed to save question');
    }
  };

  const editQuestion = (question) => {
    setCurrentQuestion({
      type: question.type,
      question: question.question,
      options: question.options || [{ text: '', isCorrect: false }, { text: '', isCorrect: false }],
      correctAnswer: question.correctAnswer || '',
      points: question.points || 1,
      explanation: question.explanation || ''
    });
    setEditingQuestionId(question?._id);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await deleteQuestion(quizId, questionId);
      setQuestions(prev => prev.filter(q => q?._id !== questionId));
      toast.success('Question deleted successfully');
      if (editingQuestionId === questionId) resetQuestionForm();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.title.trim()) {
      toast.error('Please enter a quiz title');
      setIsSubmitting(false);
      return;
    }

    try {
      let result;
      if (isEditing && quizId) {
        result = await updateQuiz(quizId, formData);
        if (result?.success) {
          toast.success('Quiz updated successfully!');
        }
        if (result?.quiz?._id) {
          // Save to localStorage for course page to pick up
          localStorage.setItem(`lesson-${lessonId}-quiz`, result?.quiz?._id);
        }
      } else if (lessonId) {
        // Create quiz first (without questions)
        const payload = { ...formData };
        result = await createQuiz(payload);

        if (result?.quiz?._id) {
          // Save to localStorage for course page to pick up
          localStorage.setItem(`lesson-${lessonId}-quiz`, result?.quiz?._id);
          setIsQuizCreated(true);
          setIsEditing(true);
          // Navigate to edit mode with quizId
          navigate(`/instructor/edit-quiz/${lessonId}/${result?.quiz?._id}`, { replace: true });
          return;
        }
      }

      if (result?.success) {
        toast.success(`${isEditing ? 'Quiz updated' : 'Quiz created'} successfully!`);
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} quiz`);
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} quiz`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading data</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
            <button
              onClick={() => {
                loadQuizData(quizId);
              }}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="max-w-6xl mx-auto p-6 relative">
        {isLoading && (
          <div className="absolute inset-0 backdrop-blur-sm bg-black/30 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            <p className="mt-3 text-white text-sm font-medium">Loading...</p>
          </div>
        )}
        {/* Header with Back Button */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <button
              onClick={() => {
                navigate(-1);
              }}
              className="flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 mb-2"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Course Builder
            </button>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
              {isEditing ? 'Edit Quiz' : 'Create New Quiz'}
            </h1>
          </div>
        </div>

        <form onSubmit={handleCreateQuiz} className="space-y-8">
          {/* Quiz Settings */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Quiz Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Time Limit (minutes)</label>
                <input
                  type="number"
                  name="timeLimit"
                  min="1"
                  value={formData.timeLimit}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Attempts Allowed</label>
                <input
                  type="number"
                  name="attemptsAllowed"
                  min="1"
                  value={formData.attemptsAllowed}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Passing Score (%)</label>
                <input
                  type="number"
                  name="passingScore"
                  min="0"
                  max="100"
                  value={formData.passingScore}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {isQuizCreated ? (
            <></>
          ) : (
            <div className="text-center text-neutral-500">Create a quiz first to add questions</div>
          )}

          {/* Create Quiz Button (Only show if not created yet) */}
          {!isQuizCreated && (
            <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {isSubmitting ? 'Creating Quiz...' : 'Create Quiz'}
              </button>
            </div>
          )}
        </form>

        {/* Questions Section (Only show after quiz is created) */}

        {isQuizCreated && (
          <div className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* LEFT: Questions List */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Questions ({questions?.length})</h2>
                {questions?.length > 0 ? (
                  <div className="space-y-4">
                    {questions.map((q, idx) => (
                      <div key={q?._id} className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                            Question {idx + 1}
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => editQuestion(q)}
                              className="text-primary-500 hover:text-primary-700 text-xs"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteQuestion(q?._id)}
                              className="text-danger-500 hover:text-danger-700 text-xs"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <p className="text-neutral-900 dark:text-white mb-2">{q.question}</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Type: {q.type} | Points: {q.points}
                        </p>
                        {q.type === 'multiple-choice' && (
                          <ul className="mt-2 text-xs">
                            {q.options.map((opt, optIndex) => (
                              <li key={optIndex} className={opt.isCorrect ? 'text-success-600 dark:text-success-400' : ''}>
                                {optIndex + 1}. {opt.text} {opt.isCorrect && '(Correct)'}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                    No questions added yet
                  </div>
                )}
              </div>

              {/* RIGHT: Question Form */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                <h3 className="text-md font-medium text-neutral-900 dark:text-white mb-4">
                  {editingQuestionId ? 'Edit Question' : 'Add New Question'}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Question Type</label>
                    <select
                      value={currentQuestion.type}
                      onChange={(e) => handleQuestionChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="multiple-choice">Multiple Choice</option>
                      <option value="true-false">True/False</option>
                      <option value="short-answer">Short Answer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Question Text *</label>
                    <textarea
                      value={currentQuestion.question}
                      onChange={(e) => handleQuestionChange('question', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter question text"
                    />
                  </div>

                  {/* MCQ Options */}
                  {currentQuestion.type === 'multiple-choice' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Options</label>
                      {currentQuestion.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={opt.text}
                            onChange={(e) => handleOptionChange(idx, e.target.value)}
                            className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder={`Option ${idx + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => toggleCorrectOption(idx)}
                            className={`px-3 py-1 text-xs font-medium rounded ${opt.isCorrect
                              ? 'bg-success-500 text-white'
                              : 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
                              }`}
                          >
                            {opt.isCorrect ? 'Correct' : 'Set Correct'}
                          </button>
                          {currentQuestion.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(idx)}
                              className="text-danger-500 hover:text-danger-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addOption}
                        className="text-primary-500 hover:text-primary-700 text-sm"
                      >
                        + Add Option
                      </button>
                    </div>
                  )}

                  {/* True/False */}
                  {currentQuestion.type === 'true-false' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Correct Answer</label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={currentQuestion.correctAnswer === 'True'}
                            onChange={() => handleQuestionChange('correctAnswer', 'True')}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">True</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            checked={currentQuestion.correctAnswer === 'False'}
                            onChange={() => handleQuestionChange('correctAnswer', 'False')}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">False</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Short Answer */}
                  {currentQuestion.type === 'short-answer' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Correct Answer (for auto-grading)
                      </label>
                      <input
                        type="text"
                        value={currentQuestion.correctAnswer}
                        onChange={(e) => handleQuestionChange('correctAnswer', e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Enter expected answer"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Points</label>
                      <input
                        type="number"
                        min="1"
                        value={currentQuestion.points}
                        onChange={(e) => handleQuestionChange('points', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Explanation (Optional)</label>
                      <textarea
                        value={currentQuestion.explanation}
                        onChange={(e) => handleQuestionChange('explanation', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Explanation shown after answering"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={saveQuestion}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {editingQuestionId ? 'Update Question' : 'Add Question'}
                    </button>
                    {editingQuestionId && (
                      <button
                        type="button"
                        onClick={resetQuestionForm}
                        className="px-4 py-2 bg-neutral-500 hover:bg-neutral-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Final Save Button (for editing mode) */}
            {isEditing && (
              <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={handleCreateQuiz}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {isSubmitting ? 'Updating Quiz...' : 'Update Quiz'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InstructorQuizCreatePage;
